import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { FaPlus } from 'react-icons/fa';
import { IoIosWarning } from 'react-icons/io';
import { Collapse, Menu, MenuItem } from '@material-ui/core';
import { BiChevronDown, BiDotsVerticalRounded, BiX } from 'react-icons/bi';

import Toggle from '../Toggle';
import Button from '../Button';
import SimpleProductRow from '../SimpleProductRow';
import { ModalHandles } from '../Modal';

import {
  IProduct,
  IProductPrice,
  IStock,
  IUnitOfMeasurement,
} from '../../models';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import ProductsService from '../../services/ProductsService';

import APIError from '../../errors/APIError';

import {
  Container,
  Table,
  TableHeader,
  TableCell,
  TableBody,
  ActionButton,
  Modal,
  ModalHeader,
  CreateNewProductButtonContainer,
} from './styles';

export type Product = Pick<
  IProduct,
  'id' | 'code' | 'name' | 'order' | 'pauseSale' | 'image' | 'grids'
> & {
  price?: Pick<IProductPrice, 'value'>;
  stock?: Pick<IStock, 'minimumStock' | 'currentStock'>;
  numOfGrids: number;
  minGridsPrice?: Pick<IProductPrice, 'value'>;
  unitOfMeasurement: Pick<IUnitOfMeasurement, 'acronym'>;
};

type SimpleGroupAccordionProps = {
  groupId: number;
  groupName: string;
  pauseSale: boolean;
  products?: Product[];
  onEdit(): void;
  onDelete(): void;
  onCreateProduct(): void;
  onEditProduct(productId: number): void;
  onCloneProduct(productId: number): void;
  onChangePauseSale(value: boolean): void;
  onDeleteProduct(productId: number): void;
  onSortProducts(products?: Product[]): void;
  onChangeProductPauseSale(productId: number, value: boolean): void;
  onEditProductImage(productId: number, productImage?: string): void;
};

const SimpleGroupAccordion: React.FC<SimpleGroupAccordionProps> = ({
  groupId,
  products,
  groupName,
  pauseSale,
  onEdit,
  onDelete,
  onEditProduct,
  onSortProducts,
  onCloneProduct,
  onCreateProduct,
  onDeleteProduct,
  onChangePauseSale,
  onEditProductImage,
  onChangeProductPauseSale,
}) => {
  const modalRef = useRef<ModalHandles>(null);
  const optionsButtonRef = useRef<HTMLButtonElement>(null);

  const { user, findRotinas } = useAuth();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const hasPermission = useCallback(
    (): boolean => findRotinas('grupos')?.alterar || false,
    [findRotinas],
  );

  const handleOnDragEnd = useCallback(
    async (dropResult: DropResult) => {
      if (!hasPermission()) {
        addToast({
          type: 'error',
          title: 'Não autorizado.',
          description: 'Você não tem permissão.',
        });
        return;
      }

      setIsLoading(true);

      const initialProducts = products;

      try {
        const { destination, source } = dropResult;

        if (
          typeof destination?.index === 'number' &&
          source.index !== destination.index &&
          products
        ) {
          let sortedProducts = Array.from(products);
          const [removedProduct] = sortedProducts.splice(source.index, 1);

          sortedProducts.splice(destination.index, 0, removedProduct);

          sortedProducts = sortedProducts.map((product, productIndex) => ({
            ...product,
            order: productIndex + 1,
          }));

          onSortProducts(sortedProducts);

          await ProductsService.sortProducts(
            user.empresa_id,
            groupId,
            sortedProducts.map((sortedProduct) => ({
              id: sortedProduct.id,
              order: sortedProduct.order,
            })),
          );
        }
      } catch (error) {
        onSortProducts(initialProducts);

        if (error instanceof APIError) {
          addToast({
            title: error.message,
            type: 'error',
          });

          return;
        }

        addToast({
          title: 'Ocorreu um erro durante a operação',
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [user.empresa_id, groupId, products, onSortProducts, addToast],
  );

  const handleToggleAccordion = useCallback(() => {
    setIsExpanded((oldState) => !oldState);
  }, []);

  const handleOpenOptions = useCallback(() => {
    setIsOptionsOpen(true);
  }, []);

  const handleCloseOptions = useCallback(() => {
    setIsOptionsOpen(false);
  }, []);

  const handleEditGroup = useCallback(() => {
    handleCloseOptions();
    onEdit();
  }, [onEdit, handleCloseOptions]);

  const handleAttemptToDeleteGroup = useCallback(() => {
    modalRef.current?.open();
    handleCloseOptions();
  }, [handleCloseOptions]);

  const handleDeleteGroup = useCallback(() => {
    modalRef.current?.close();
    onDelete();
  }, [onDelete]);

  useEffect(() => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  }, [products]);

  return (
    <Container>
      <header>
        <div>
          <h1>{groupName}</h1>
          <div>
            <Toggle
              checked={!pauseSale}
              firstText="Pausado"
              secondText="Ativado"
              onToggle={(value) => {
                if (!hasPermission()) {
                  addToast({
                    type: 'error',
                    title: 'Não autorizado.',
                    description: 'Você não tem permissão.',
                  });
                  return;
                }
                onChangePauseSale(value);
              }}
            />
            {hasPermission() && (
              <>
                <ActionButton
                  isExpanded={isExpanded}
                  ref={optionsButtonRef}
                  type="button"
                  onClick={handleOpenOptions}
                >
                  <BiDotsVerticalRounded />
                </ActionButton>
                <Menu
                  id={`simple-group-${groupId}-options-menu`}
                  anchorEl={optionsButtonRef.current}
                  keepMounted
                  open={isOptionsOpen}
                  onClose={handleCloseOptions}
                  PaperProps={{
                    style: {
                      maxHeight: 48 * 4.5,
                      width: '20ch',
                    },
                  }}
                >
                  <MenuItem onClick={handleEditGroup}>Editar</MenuItem>
                  <MenuItem onClick={handleAttemptToDeleteGroup}>
                    Excluir
                  </MenuItem>
                </Menu>
              </>
            )}
            <ActionButton
              isExpanded={isExpanded}
              className="arrow"
              type="button"
              onClick={handleToggleAccordion}
            >
              <BiChevronDown />
            </ActionButton>
          </div>
        </div>
      </header>
      <main>
        <Collapse in={isExpanded}>
          <Table>
            <hr />
            <TableHeader>
              <TableCell>Item</TableCell>
              <TableCell>Código PDV</TableCell>
              <TableCell>Estoque</TableCell>
              <TableCell>Preço</TableCell>
              <TableCell>Status de venda</TableCell>
              <TableCell />
            </TableHeader>
            <TableBody>
              {products && (
                <DragDropContext
                  onDragEnd={(dragResult) => handleOnDragEnd(dragResult)}
                >
                  <Droppable droppableId="droppableProducts">
                    {(droppableProvided) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                      >
                        {products
                          .sort((a, b) => (a?.order ?? 1) - (b?.order ?? 1))
                          .map((product, productIndex) => (
                            <Draggable
                              key={product.id}
                              draggableId={String(product.id)}
                              isDragDisabled={isLoading}
                              index={productIndex}
                            >
                              {(draggableProvided) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                >
                                  <SimpleProductRow
                                    product={product}
                                    isLoading={isLoading}
                                    isExpanded={isExpanded}
                                    onEdit={() => onEditProduct(product.id)}
                                    onClone={() => onCloneProduct(product.id)}
                                    onDelete={() => onDeleteProduct(product.id)}
                                    onEditImage={() =>
                                      onEditProductImage(
                                        product.id,
                                        product.image,
                                      )
                                    }
                                    onChangePauseSale={() =>
                                      onChangeProductPauseSale(
                                        product.id,
                                        !product.pauseSale,
                                      )
                                    }
                                    dragHandleProps={
                                      draggableProvided.dragHandleProps
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                        {droppableProvided.placeholder}
                        {hasPermission() && (
                          <CreateNewProductButtonContainer>
                            <Button
                              onClick={onCreateProduct}
                              variant="outlined"
                            >
                              <FaPlus />
                              Adicionar produto
                            </Button>
                          </CreateNewProductButtonContainer>
                        )}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </TableBody>
          </Table>
        </Collapse>
      </main>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Ao realizar a ação todos os dados do grupo &quot;{groupName}&quot; e
          seus produtos serão excluídos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleDeleteGroup}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default SimpleGroupAccordion;
